import { Stack } from '@chronosphereio/chrono-ui';
import {
  ChartBoxPlusOutline as AddPanelIcon,
  ArrowDown as ArrowDownIcon,
  ArrowUp as ArrowUpIcon,
  ContentCopy,
  DeleteOutline as DeleteIcon,
  PencilOutline as PencilIcon,
  ViewDashboardVariantOutline,
} from 'mdi-material-ui';
import { ARIA_LABEL_TEXT, DASHBOARD_TOOLTIP_TEXT } from '@/dashboards/constants';
import {
  PanelGroupId,
  useDeletePanelGroupDialog,
  useEditMode,
  usePanelGroup,
  usePanelGroupActions,
} from '@/dashboards/context';
import { useAnalytics } from '@/plugin-system';
import { ActionButton } from '../ActionButton';
import { CONTENT_COPY_ICON_SCALE } from '../Panel/PanelEditActions';

const EDIT_DYNAMIC_GROUP_HELP_TEXT = 'Dynamic panel group options are propagated from the main instance above.';

export interface GridActionProps {
  title: string;
  groupId: PanelGroupId;
  isActionDisabled?: boolean;
  showMoveControls?: boolean;
  isMainPanelGroup?: boolean;
}

export function GridActions({
  groupId,
  title,
  isActionDisabled = false,
  showMoveControls = true,
  isMainPanelGroup = true,
}: GridActionProps) {
  const { isEditMode } = useEditMode();
  const group = usePanelGroup(groupId);
  const { openAddPanel, openEditPanelGroup, duplicatePanelGroup, openCleanUpLayout, moveUp, moveDown } =
    usePanelGroupActions(groupId);
  const { openDeletePanelGroupDialog } = useDeletePanelGroupDialog();
  const analytics = useAnalytics();

  if (!isEditMode) {
    return null;
  }

  return (
    <Stack data-testid="grid-actions" direction="row" spacing={0.5}>
      {group.kind === 'Grid' && (
        <ActionButton
          size="small"
          label={ARIA_LABEL_TEXT.addPanelToGroup(title)}
          helpText={!isMainPanelGroup ? EDIT_DYNAMIC_GROUP_HELP_TEXT : DASHBOARD_TOOLTIP_TEXT.addPanelToGroup}
          isDisabled={isActionDisabled}
          onClick={(e) => {
            e.stopPropagation();
            openAddPanel();
          }}
          icon={<AddPanelIcon />}
        />
      )}
      <ActionButton
        size="small"
        label={ARIA_LABEL_TEXT.editGroup(title)}
        helpText={!isMainPanelGroup ? EDIT_DYNAMIC_GROUP_HELP_TEXT : DASHBOARD_TOOLTIP_TEXT.editGroup}
        isDisabled={isActionDisabled}
        onClick={(e) => {
          e.stopPropagation();
          openEditPanelGroup();
        }}
        icon={<PencilIcon />}
      />
      <ActionButton
        size="small"
        label={ARIA_LABEL_TEXT.duplicateGroup(title)}
        helpText={!isMainPanelGroup ? EDIT_DYNAMIC_GROUP_HELP_TEXT : DASHBOARD_TOOLTIP_TEXT.duplicateGroup}
        isDisabled={isActionDisabled}
        onClick={(e) => {
          e.stopPropagation();
          duplicatePanelGroup();
        }}
        icon={<ContentCopy sx={{ transform: `scale(${CONTENT_COPY_ICON_SCALE})` }} />}
      />
      {group.kind === 'Grid' && (
        <ActionButton
          size="small"
          label={ARIA_LABEL_TEXT.cleanUpLayout(title)}
          helpText={!isMainPanelGroup ? EDIT_DYNAMIC_GROUP_HELP_TEXT : DASHBOARD_TOOLTIP_TEXT.cleanUpLayout}
          isDisabled={isActionDisabled}
          onClick={(e) => {
            e.stopPropagation();
            openCleanUpLayout();
            analytics?.track('dashboard-clean_up_layout_clicked');
          }}
          icon={<ViewDashboardVariantOutline />}
        />
      )}
      <ActionButton
        size="small"
        label={ARIA_LABEL_TEXT.deleteGroup(title)}
        helpText={!isMainPanelGroup ? EDIT_DYNAMIC_GROUP_HELP_TEXT : DASHBOARD_TOOLTIP_TEXT.deleteGroup}
        isDisabled={isActionDisabled}
        onClick={(e) => {
          e.stopPropagation();
          openDeletePanelGroupDialog(groupId);
        }}
        icon={<DeleteIcon />}
      />
      {showMoveControls && (
        <>
          <ActionButton
            size="small"
            label={ARIA_LABEL_TEXT.moveGroupDown(title)}
            helpText={DASHBOARD_TOOLTIP_TEXT.moveGroupDown}
            isDisabled={moveDown === undefined}
            onClick={(e) => {
              e.stopPropagation();
              moveDown?.();
            }}
            icon={<ArrowDownIcon />}
          />
          <ActionButton
            size="small"
            label={ARIA_LABEL_TEXT.moveGroupUp(title)}
            helpText={DASHBOARD_TOOLTIP_TEXT.moveGroupUp}
            isDisabled={moveUp === undefined}
            onClick={(e) => {
              e.stopPropagation();
              moveUp?.();
            }}
            icon={<ArrowUpIcon />}
          />
        </>
      )}
    </Stack>
  );
}
